import { ApolloClient, ApolloError, ApolloQueryResult, NetworkStatus } from '@apollo/client';
import merge from 'deepmerge';
import { DocumentNode, getOperationAST } from 'graphql';
import { logError } from 'lib/utilities/logger';
import { GraphServer } from './constants';

const DEFAULT_OPTIONS = {
	errorPolicy: 'all',
	fetchPolicy: 'no-cache',
} as const;

export function getOperationName(document: DocumentNode): string | undefined {
	const operationAST = getOperationAST(document, undefined);
	return operationAST?.name?.value;
}

export const executeQuery = async <TCacheShape, TQuery, TVariables>(
	apolloClient: ApolloClient<TCacheShape>,
	service: GraphServer,
	query: DocumentNode,
	variables?: TVariables,
	context?: Record<string, unknown>,
): Promise<ApolloQueryResult<TQuery>> => {
	if (!context) {
		context = {};
	}
	context.server = service;
	if (!context.headers) {
		context.headers = {};
	}
	context.headers['apollographql-query-name'] = getOperationName(query);

	return await apolloClient.query<TQuery, TVariables>(
		merge(DEFAULT_OPTIONS, {
			query,
			variables,
			context,
		}),
	);
};

export const executeQueryIgnoreError = async <TCacheShape, TQuery, TVariables>(
	apolloClient: ApolloClient<TCacheShape>,
	service: GraphServer,
	query: DocumentNode,
	variables?: TVariables,
	context?: Record<string, unknown>,
): Promise<ApolloQueryResult<TQuery>> => {
	try {
		return await executeQuery(apolloClient, service, query, variables, context);
	} catch (error) {
		logError(error);
		return {
			data: null,
			loading: false,
			networkStatus: NetworkStatus.error,
			error: error as ApolloError,
		};
	}
};
